/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use pre-registered patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type patients and institutions 
 */
import {
    IInstitution,
    useFormByIds,
    useFormMetadata
} from '@ngt/opms';

import { Chip, Skeleton, styled } from '@mui/material';

/*
 * Used to format datetimes
 */
import { DateTime } from 'luxon';

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';






/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import { FC } from 'react';
import * as Dtos from '../api/dtos';
/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const ScreeningFailedReasonCell: FC<GridRenderCellParams<Dtos.Patient>> = ({ row: patient }) => {
    const screeningFormMetadata = useFormMetadata(1); // screening form
    const { data, loading } = useFormByIds<Dtos.ScreeningForm>(screeningFormMetadata, patient.institutionId, patient.id, 1 /* screening */, 1, 1, false, false, false, { dedupingInterval: 100000000000 });

    return (
        <>
            {
                loading ?
                    <Skeleton /> :
                        data?.screenFailReason === Dtos.LookupScreenFailReasonType.ParticipantIneligible ? 'Participant ineligible for registration' :
                            data?.screenFailReason === Dtos.LookupScreenFailReasonType.ClinicianDeemedUnsuitable ? 'Clinician/MDT deemed unsuitable' : 
                                data?.screenFailReason === Dtos.LookupScreenFailReasonType.ParticipantPrefersStandardCare ? 'Participant declined trial because they prefer standard of care' : 
                                    data?.screenFailReason === Dtos.LookupScreenFailReasonType.ParticipantDeclinedNoReason ? 'Participant declined no reason given or not interested in the trial' : 'Unknown'
            }
        </>
    );
}

const useFailedScreeningGridColDefs = (
    institutions: IInstitution[] | null, 
    showInitials: boolean, 
    showSite: boolean
): GridColDef<Dtos.Patient>[] => {
    const columns = React.useMemo(() => {
        const c: (GridColDef<Dtos.Patient> | boolean)[] = [
            {
                field: 'studyNumber',
                headerName: 'Temp. No',
                width: 140
            },
            showInitials && {
                field: 'initials',
                headerName: 'Initials',
                //width: 200
            },
            {
                field: 'institutionCode',
                headerName: 'Site Code',
                renderCell: rowData => (
                    <StyledChip
                        label={rowData.row.institutionCode}
                        size="small"
                    />
                ),
                width: 110
            },
            {
                field: 'institutionId',
                headerName: 'Institution',
                valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
                flex: 1
            },
            {
                field: 'screeningDate',
                headerName: 'Date Screened',
                renderCell: rowData => (
                    rowData.row.screeningDate ? DateTime.fromISO(rowData.row.screeningDate).toFormat('dd/MM/yyyy') : null
                ),
                width: 150
            },
            {
                field: 'id',
                headerName: 'Reason',
                sortable: false,
                filterable: false,
                renderCell: rowData => (
                    <ScreeningFailedReasonCell {...rowData} />
                ),
                flex: 1
            },
        ];

        return c.filter(column => !!column) as GridColDef<Dtos.Patient>[];
    }, [showSite, institutions, showInitials]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useFailedScreeningGridColDefs;